import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import { supabase, generateSignedUrl } from '../services/supabaseClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../services/apiService';
import '../styles/ChatInterface.css';
import CitationModal from './CitationModal';
import parse from 'html-react-parser';
import { toast } from 'react-hot-toast';

const ChatInterface = ({ onSummaryUpdate }) => {
  const api = useAPI();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const chatId = new URLSearchParams(location.search).get('chatId');
  const [caseId, setCaseId] = useState(null);
  const [zingPrompts, setZingPrompts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setFileUrl] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [fileMapping, setFileMapping] = useState({});
  const [currentModalFileName, setCurrentModalFileName] = useState('');

  const { user, sessionStatus, refreshSession } = useAuth();

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (chatId && user) {
      fetchCaseIdFromChat();
      fetchZingPrompts();
    }
  }, [chatId, user]);

  const fetchCaseIdFromChat = async () => {
    try {
      const response = await api.get(`/chat/${chatId}/case`);
      if (response.data?.case_id) {
        setCaseId(response.data.case_id);
      }
    } catch (error) {
      console.error('Error fetching case ID:', error);
      setError('Failed to load case details.');
    }
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (caseId) {
      updateFileMapping();
    }
  }, [caseId]);

  const fetchChatDetails = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('chats')
        .select('case_id, summary')
        .eq('chat_id', chatId)
        .single();

      if (error) throw error;

      if (data && data.case_id) {
        setCaseId(data.case_id);
      } else {
        throw new Error('Case ID not found in chat details');
      }
      
      await fetchChatHistory();

    } catch (error) {
      console.error('Error fetching chat details:', error);
      setError('Failed to load chat details.');
    }
  };

  const fetchChatHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const formattedMessages = data.map(message => ({
        type: message.role_id === 1 ? 'user' : 'ai',
        content: message.content
      }));

      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const fetchZingPrompts = async () => {
    try {
      const response = await api.get('/zing_prompts/list');
      setZingPrompts(response.data);
    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error fetching zing prompts:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputMessage.trim() === '') return;

    await updateFileMapping(); // Refresh the mapping
    await sendMessageToModel(inputMessage);
  };

  const sendMessageToModel = async (message) => {
    if (!user || !caseId || !chatId) {
      console.error('User not authenticated, caseId or chatId not available');
      navigate('/login');
      return;
    }

    const newMessage = { type: 'user', content: message };
    setMessages(prevMessages => [...prevMessages, newMessage]);
    setInputMessage('');

    try {
      // Add user message to chat_messages
      const { error: userMessageError } = await supabase
        .from('chat_messages')
        .insert({ chat_id: chatId, role_id: 1, content: message });

      if (userMessageError) throw userMessageError;

      // If this is the first message, generate a summary
      if (messages.length === 0) {
        generateSummary(message);
      }

      // Get fresh session for fetch request
      const session = await refreshSession();
      if (!session?.access_token) {
        throw new Error('No valid session');
      }

      const response = await fetch(`${api.defaults.baseURL}/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'X-Refresh-Token': session.refresh_token
        },
        body: JSON.stringify({
          message: message,
          case_id: caseId,
          chat_id: chatId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let aiResponse = '';
      let isFirstToken = true;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            aiResponse += data.response;

            if (isFirstToken) {
              setMessages(prevMessages => [...prevMessages, { type: 'ai', content: aiResponse }]);
              isFirstToken = false;
            } else {
              setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage && lastMessage.type === 'ai') {
                  lastMessage.content = aiResponse;
                }
                return updatedMessages;
              });
            }
          }
        }
      }

      // Add final AI response to chat_messages in Supabase
      const { error: aiMessageError } = await supabase
        .from('chat_messages')
        .insert({ chat_id: chatId, role_id: 2, content: aiResponse });

      if (aiMessageError) throw aiMessageError;

    } catch (error) {
      console.error('Error executing zing prompt:', error);
      setError(`Error: ${error.message}`);
      setMessages(prevMessages => [...prevMessages, { type: 'error', content: `Error: ${error.message}` }]);
    }
  };

  const generateSummary = async (message) => {
    try {
      const response = await api.post('/generate_summary/', {
        message: message,
        chat_id: chatId,
        case_id: caseId
      });

      onSummaryUpdate(caseId);
    } catch (error) {
      console.error('Error generating summary:', error);
    }
  };

  const handleZingPromptClick = useCallback(async (prompt) => {
    setError(null);
    
    const newMessage = { type: 'user', content: prompt.display_text };
    setMessages(prevMessages => [...prevMessages, newMessage]);

    try {
      if (!caseId) {
        throw new Error('Case ID is not available');
      }

      // Add user message to chat_messages
      const { error: userMessageError } = await supabase
        .from('chat_messages')
        .insert({ chat_id: chatId, role_id: 1, content: prompt.display_text });

      if (userMessageError) throw userMessageError;

      const response = await fetch(`${api.defaults.baseURL}/zing_prompts/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          chat_id: chatId,  // Add this line
          zing_prompt_id: prompt.zing_prompt_id,
          special_query_instructions: ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(errorData.detail)}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let aiResponse = '';
      let isFirstToken = true;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            if (data.error) {
              throw new Error(data.error);
            }
            aiResponse += data.response;

            if (isFirstToken) {
              setMessages(prevMessages => [...prevMessages, { type: 'ai', content: aiResponse }]);
              isFirstToken = false;
            } else {
              setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage && lastMessage.type === 'ai') {
                  lastMessage.content = aiResponse;
                }
                return updatedMessages;
              });
            }
          }
        }
      }

      // Add final AI response to chat_messages in Supabase
      const { error: aiMessageError } = await supabase
        .from('chat_messages')
        .insert({ chat_id: chatId, role_id: 2, content: aiResponse });

      if (aiMessageError) throw aiMessageError;

    } catch (error) {
      console.error('Error executing zing prompt:', error);
      setError(`Error: ${error.message}`);
      setMessages(prevMessages => [...prevMessages, { type: 'error', content: `Error: ${error.message}` }]);
    }
  }, [caseId, chatId, setError, setMessages]);  // Add chatId to the dependency array

  const fetchCaseDetails = async (fileId) => {
    try {
      const { data, error } = await supabase
        .from('case_files')
        .select('case_id, bucket_url')
        .eq('file_id', fileId)
        .single();

      if (error) {
        console.error('Error fetching case details:', error);
        return null;
      }

      const fileName = data.bucket_url ? data.bucket_url.split('/').pop() : 'Unknown file';
      return { ...data, fileName };
    } catch (error) {
      console.error('Error fetching case details:', error);
      return null;
    }
  };

  const handleCitationClick = async (fileId, page) => {
    if (!fileId) {
      console.error('Invalid fileId:', fileId);
      return;
    }

    try {
      const fileName = fileMapping[fileId] || 'Unknown file';
      setCurrentModalFileName(fileName);

      const caseDetails = await fetchCaseDetails(fileId);
      if (!caseDetails) return;

      const { bucket_url } = caseDetails;
      const bucketName = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;

      const signedUrl = await generateSignedUrl(bucketName, bucket_url);
      if (signedUrl) {
        setFileUrl(signedUrl);  
        setPageNumber(page);
        setIsModalOpen(true);
      }

      // Store fileName in the message object
      setMessages(prevMessages => prevMessages.map(message => {
        if (message.content.includes(`file_id="${fileId}"`)) {
          return { ...message, fileName };
        }
        return message;
      }));

    } catch (error) {
      console.error('Error handling citation click:', error);
    }
  };

  const shortenFileName = (fileName) => {
    if (fileName.length <= 32) return fileName;
    const start = fileName.slice(0, 25);
    const end = fileName.slice(-7);
    return `${start}...${end}`;
  };

  const renderMessageContent = (content) => {
    return parse(content, {
      replace: (node) => {
        if (node.name === 'citation') {
          const fileId = node.attribs.file_id?.replace(/[:,]/g, '').trim();
          const page = node.attribs.page_number?.replace(/[:,]/g, '').trim();
          const citedText = node.children && node.children[0] ? node.children[0].data : '';
          const fileName = fileMapping[fileId] || 'Unknown file';
          const shortenedFileName = shortenFileName(fileName);

          return (
            <span className="citationTooltip">
              <span
                className="highlightedText"
                onClick={() => handleCitationClick(fileId, page)}
              >
                {citedText}
              </span>
              <span className="tooltipText">
                <span className="fileLine">
                  <span className="underline">File</span>: {shortenedFileName}
                </span><br />
                <span className="fileLine">
                  <span className="underline">Page</span>: {page}
                  <span style={{ color: '#ccc', fontStyle: 'italic', marginLeft: '25px' }}>(click to view)</span>
                </span><br />
              </span>
            </span>
          );
        }
      },
    });
  };

  const updateFileMapping = async () => {
    try {
      const { data, error } = await supabase
        .from('case_files')
        .select('file_id, bucket_url')
        .eq('case_id', caseId);

      if (error) {
        console.error('Error fetching file mapping:', error);
        return;
      }

      const newMapping = data.reduce((acc, file) => {
        const fileName = file.bucket_url ? file.bucket_url.split('/').pop() : 'Unknown file';
        acc[file.file_id] = fileName;
        return acc;
      }, {});

      setFileMapping(newMapping);
    } catch (error) {
      console.error('Error updating file mapping:', error);
    }
  };

  if (!user) {
    return <div>Please sign in to use the chat interface.</div>;
  }

  return (
    <div className="chatContainer">
      {caseId && (
        <button 
          onClick={() => navigate(`/cases/${caseId}/chronology`)}
          className="back-to-chronology-btn"
        >
          Back to Chronology
        </button>
      )}
      <div className="chatHistory">
        {messages.length === 0 ? (
          <div className="emptyChat">
            <div className="emptyChatContent">
              <h2 className="emptyChatTitle">What can Pari help you with?</h2>
              <div className="examplePromptsGrid">
                {zingPrompts.map((prompt) => (
                  <div 
                    key={prompt.zing_prompt_id} 
                    className="examplePrompt"
                    onClick={() => handleZingPromptClick(prompt)}
                  >
                    <p className="examplePromptText">{prompt.display_text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`messageContainer ${message.type === 'user' ? 'userMessage' : 'aiMessage'}`}>
                <span className="messageContent">
                  {renderMessageContent(message.content)}
                </span>
              </div>
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <CitationModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        fileUrl={pdfUrl}  // Change this line from pdfUrl to fileUrl
        pageNumber={pageNumber}
        filename={currentModalFileName}
      />

      <div className="inputContainer">
        <form onSubmit={handleSendMessage} className="inputForm">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="What can Pari help you with?"
            className="inputField"
          />
          <button type="submit" className="sendButton">
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
